<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue';
import { FhSpinner, FhTextInput } from '@fareharbor-com/beacon-vue';
import { location } from '@fareharbor-com/beacon-vue/icons';
import { Logger } from '@/common/Logger';
import type { GoogleMapsSuggestion } from '@/common/api/googleMaps';
import {
  getGoogleMapsAutocomplete,
  getGoogleMapsGeocode,
} from '@/common/api/googleMaps';
import { uniqueId } from '@/common/uniqueId';
import LocationSearchSuggestion from './LocationSearchSuggestion.vue';

interface SearchBarProps {
  initialText?: string;
  placeholder?: string;
  icon?: Component;
}

const props = defineProps<SearchBarProps>();
const emit = defineEmits(['submitSearch', 'clearSearch', 'submitGoogleSearch']);

const id = uniqueId('search-bar');
const text = ref(props.initialText || '');
const highlightedIndex = ref(-1);
const containerRef = ref<HTMLElement | null>(null);
const loadingGoogle = ref(false);
const errorGoogle = ref<string | null>(null);
const googleSuggestions = ref<GoogleMapsSuggestion[]>([]);
const debounceTimeout = ref<number | null>(null);
const showSuggestions = ref(false);

// Handle clicks outside the component to hide suggestions
const handleClickOutside = (event: MouseEvent) => {
  if (
    containerRef.value &&
    !containerRef.value.contains(event.target as Node)
  ) {
    googleSuggestions.value = [];
    showSuggestions.value = false;
  }
};

// Add event listener on mount and remove on unmount
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  // Clear any pending debounce timeout
  if (debounceTimeout.value !== null) {
    window.clearTimeout(debounceTimeout.value);
  }
});

// Update form submit handler
const handleFormSubmit = () => {
  // Pass text with empty coordinates for direct searches
  emit('submitSearch', {
    text: text.value,
    coordinates: null,
  });
};

const fetchGoogleSuggestions = async (input: string) => {
  if (!input.trim()) {
    googleSuggestions.value = [];
    showSuggestions.value = false;
    return;
  }

  loadingGoogle.value = true;
  errorGoogle.value = null;
  showSuggestions.value = true;

  try {
    const response = await getGoogleMapsAutocomplete(input);
    if (response.data) {
      googleSuggestions.value = response.data;
      // Automatically highlight the first suggestion if there are suggestions
      if (googleSuggestions.value.length > 0) {
        highlightedIndex.value = 0;
      }
    } else {
      googleSuggestions.value = [];
    }
  } catch (err) {
    errorGoogle.value =
      err instanceof Error ? err.message : 'An error occurred';
    Logger.error('[LocationSearchBar] Error fetching Google suggestions');
  } finally {
    loadingGoogle.value = false;
  }
};

// Debounce function to delay API calls while typing
const debouncedFetchSuggestions = (input: string) => {
  // Clear any existing timeout
  if (debounceTimeout.value !== null) {
    window.clearTimeout(debounceTimeout.value);
  }

  // Set a new timeout
  debounceTimeout.value = window.setTimeout(() => {
    fetchGoogleSuggestions(input);
  }, 300); // 300ms delay
};

const handleGoogleSuggestionClick = async (
  suggestion: GoogleMapsSuggestion,
) => {
  try {
    const response = await getGoogleMapsGeocode(suggestion.place_id);
    if (response.data) {
      text.value = suggestion.main_text;
      googleSuggestions.value = [];
      showSuggestions.value = false;
      highlightedIndex.value = -1;

      // Extract coordinates from the response
      const { bounds } = response.data;

      // Create search data object with coordinates
      const googleSearchData = {
        text: suggestion.main_text,
        coordinates: {
          ne_lat: bounds.northeast.lat,
          ne_lng: bounds.northeast.lng,
          sw_lat: bounds.southwest.lat,
          sw_lng: bounds.southwest.lng,
        },
      };

      // Emit with the search data object using the specific Google emit
      emit('submitGoogleSearch', googleSearchData);
    }
  } catch (err) {
    errorGoogle.value =
      err instanceof Error ? err.message : 'An error occurred';
    Logger.error('[LocationSearchBar] Error fetching geocode');
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (
    event.key === 'ArrowDown' &&
    highlightedIndex.value < googleSuggestions.value.length - 1
  ) {
    event.preventDefault();
    highlightedIndex.value += 1;
  } else if (event.key === 'ArrowUp' && highlightedIndex.value > 0) {
    event.preventDefault();
    highlightedIndex.value -= 1;
  } else if (event.key === 'Enter' && highlightedIndex.value >= 0) {
    event.preventDefault();
    handleGoogleSuggestionClick(
      googleSuggestions.value[highlightedIndex.value],
    );
  } else if (event.key === 'Enter') {
    event.preventDefault();
    handleFormSubmit();
  }
};

// Watch for input changes to fetch Google suggestions
watch(text, (newText) => {
  if (!newText) {
    googleSuggestions.value = [];
    showSuggestions.value = false;
    highlightedIndex.value = -1;
    emit('clearSearch', { text: '', coordinates: null });
  } else {
    debouncedFetchSuggestions(newText);
  }
});
</script>

<template>
  <form @submit.prevent="handleFormSubmit" class="relative w-full">
    <div ref="containerRef">
      <FhTextInput
        type="search"
        class="bg-gray-50 placeholder-gray-400 rounded-lg border-transparent focus:border-transparent focus:ring-0 w-full"
        :id="id"
        :placeholder="props.placeholder"
        v-model="text"
        @keydown="handleKeyDown"
        data-test-id="location-search-bar"
        :prefixIcon="location"
      >
        <template #suffix v-if="loadingGoogle">
          <div class="flex items-center justify-end">
            <FhSpinner size="md" />
          </div>
        </template>
      </FhTextInput>
    </div>
    <ul
      v-if="
        showSuggestions &&
        (googleSuggestions.length > 0 ||
          (text.trim() && !loadingGoogle && googleSuggestions.length === 0))
      "
      class="border border-gray-300 bg-white max-h-64 overflow-y-auto absolute z-50 mt-1 w-full rounded-lg shadow-md"
    >
      <!-- Show "No locations found" when there are no suggestions but user has searched -->
      <template
        v-if="googleSuggestions.length === 0 && text.trim() && !loadingGoogle"
      >
        <LocationSearchSuggestion
          :suggestion="{
            city: '',
            state: '',
            country: '',
            matchedFields: [],
            locationType: 0,
          }"
          :prefix="text"
          class="p-3 cursor-not-allowed opacity-60"
          :aria-disabled="true"
        />
      </template>

      <!-- Show actual Google suggestions -->
      <template
        v-for="(item, index) in googleSuggestions"
        :key="'google-' + index"
      >
        <LocationSearchSuggestion
          :suggestion="{
            city: item.main_text,
            state: '',
            country: item.secondary_text,
            matchedFields: ['city'],
            locationType: 4,
          }"
          :prefix="text"
          @click="handleGoogleSuggestionClick(item)"
          :class="{
            'p-3 cursor-pointer': true,
            'bg-gray-200': index === highlightedIndex,
          }"
          :aria-selected="index === highlightedIndex"
        />
      </template>
    </ul>
  </form>
</template>

<style scoped>
.sb-border-width {
  border-width: 1px;
}
</style>
